import equal from 'fast-deep-equal';

import type { Block, Data, Document, Inline, Text } from '@gitbook/slate';
import type { List } from '@gitbook/util-immutable';

import { yToJSON } from '../utils';
import type {
    YSlateBlock,
    YSlateData,
    YSlateDocument,
    YSlateFragment,
    YSlateInline,
    YSlateText,
} from './utils';

/**
 * Check that a Yjs document matches a Slate document.
 *
 * For now, it only checks the number of children on each node and the data property.
 * We're using it to try to track down the cause of a bug that sometimes removes data properties
 * from nodes.
 */
export function checkYDocument(document: Document, yDoc: YSlateDocument) {
    checkYNodes(document.nodes, yDoc);
}

function checkYNodes(
    nodes: List<Block | Inline | Text>,
    yNodes: YSlateDocument | YSlateFragment | YSlateInline | YSlateBlock
) {
    if (nodes.size !== yNodes.length) {
        throw new YDocumentCheckFail(`Nodes size mismatch: ${nodes.size} !== ${yNodes.length}`);
    }

    nodes.forEach((node, index) => {
        const yNode = yNodes.get(index);

        if (node.object === 'inline') {
            checkYInline(node, yNode as YSlateInline);
        } else if (node.object === 'block') {
            checkYBlock(node, yNode as YSlateBlock);
        } else {
            checkYText(node, yNode as YSlateText);
        }
    });
}

function checkYBlock(block: Block, yBlock: YSlateBlock) {
    if (block.key !== yBlock.getAttribute('key')) {
        throw new YDocumentCheckFail(
            `Block key mismatch: ${block.key} !== ${yBlock.getAttribute('key')}`
        );
    }

    if (block.type !== yBlock.getAttribute('type')) {
        throw new YDocumentCheckFail(
            `Block type mismatch: ${block.type} !== ${yBlock.getAttribute('type')}`
        );
    }

    checkYData(block.data, yBlock.getAttribute('data'));
    checkYNodes(block.nodes, yBlock);
}

function checkYInline(inline: Inline, yInline: YSlateInline) {
    if (inline.key !== yInline.getAttribute('key')) {
        throw new YDocumentCheckFail(
            `Inline key mismatch: ${inline.key} !== ${yInline.getAttribute('key')}`
        );
    }

    if (inline.type !== yInline.getAttribute('type')) {
        throw new YDocumentCheckFail(
            `Inline type mismatch: ${inline.type} !== ${yInline.getAttribute('type')}`
        );
    }

    if (inline.nodes.size !== yInline.length) {
        throw new YDocumentCheckFail(
            `Inline nodes size mismatch: ${inline.nodes.size} !== ${yInline.length}`
        );
    }

    checkYData(inline.data, yInline.getAttribute('data'));
    checkYNodes(inline.nodes, yInline);
}

function checkYText(text: Text, yText: YSlateText) {
    if (text.key !== yText.getAttribute('key')) {
        throw new YDocumentCheckFail(
            `Text key mismatch: ${text.key} !== ${yText.getAttribute('key')}`
        );
    }
}

function checkYData(data: Data, yData: YSlateData | undefined) {
    // in Yjs we don't set a data map if there is no data on the Slate node.
    if (data.size === 0 && yData === undefined) {
        return;
    }

    if (!yData) {
        throw new YDocumentCheckFail(
            `Data mismatch: Slate data size is ${data.size} but yData is undefined`
        );
    }

    if (data.size !== yData.size) {
        throw new YDocumentCheckFail(
            `Data size mismatch ${data.size} (slate) !== ${yData.size} (yjs)`
        );
    }

    data.forEach((value, key) => {
        const jsonYValue = yToJSON(yData.get(key));
        if (!equal(value, jsonYValue)) {
            throw new YDocumentCheckFail(
                `Data value ${key} mismatch`,
                JSON.stringify(value),
                JSON.stringify(jsonYValue)
            );
        }
    });
}

export class YDocumentCheckFail extends Error {
    public slateValue?: string | number;
    public yValue?: string | number;

    constructor(message: string, slateValue?: string | number, yValue?: string | number) {
        super(`YDocumentCheckFail: ${message}`);

        this.slateValue = slateValue;
        this.yValue = yValue;
    }
}
